import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import WordMark from "@/components/WordMark";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#facility" },
  { label: "Services", href: "#expertise" },
  { label: "Multi-Brand Expertise", href: "#brands" },
  { label: "OEM / OES Spares", href: "#spares" },
  { label: "Warranty", href: "#warranty" },
  { label: "Contact", href: "#contact" },
];

export default function Header({ onBook }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
    } else {
      window.__lenis?.start();
    }
  }, [open]);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollToId(href), 80);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-[60] transition-[background-color,border-color] duration-500 ${
          scrolled
            ? "border-b border-gold/10 bg-espresso/90 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <button
            onClick={() => go("#home")}
            aria-label="V-Elite Drives — Home"
            data-testid="header-logo"
          >
            <WordMark compact />
          </button>
          <div className="flex items-center gap-3">
            <a
              href="tel:+919688996889"
              data-testid="header-call-link"
              className="hidden items-center gap-2 text-xs font-semibold tracking-[0.18em] text-ivory/80 transition-colors duration-300 hover:text-gold md:flex"
            >
              <Phone size={14} className="text-gold" /> 96889 96889
            </a>
            <button
              onClick={onBook}
              data-testid="header-book-button"
              className="hidden rounded-full bg-gradient-to-br from-gold-light to-gold-deep px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-ink transition-transform duration-300 hover:scale-[1.04] md:inline-flex"
            >
              Book a Service
            </button>
            <button
              onClick={() => setOpen(true)}
              data-testid="header-menu-button"
              aria-label="Open menu"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/25 text-ivory transition-colors duration-300 hover:border-gold/60 hover:text-gold"
            >
              <Menu size={20} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-menu"
            className="fixed inset-0 z-[70] flex flex-col bg-espresso"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex items-center justify-between px-5 py-4 sm:px-8">
              <WordMark compact />
              <button
                onClick={() => setOpen(false)}
                data-testid="nav-menu-close"
                aria-label="Close menu"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/25 text-ivory transition-colors duration-300 hover:border-gold/60 hover:text-gold"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => go(l.href)}
                  className="group flex items-baseline gap-4 py-2 text-left"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <span className="font-display text-[10px] font-bold tracking-widest text-gold/60">
                    0{i + 1}
                  </span>
                  <span className="font-display text-2xl font-bold uppercase tracking-wide text-ivory transition-colors duration-300 group-hover:text-gold sm:text-3xl">
                    {l.label}
                  </span>
                </motion.button>
              ))}
            </nav>
            <motion.div
              className="flex items-center gap-4 border-t border-gold/10 px-8 py-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <a
                href="tel:+919688996889"
                data-testid="nav-call-button"
                className="flex items-center gap-2 rounded-full border border-gold/30 px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] text-ivory"
              >
                <Phone size={14} className="text-gold" /> 96889 96889
              </a>
              <a
                href="https://wa.me/919688996889?text=Hi%20V-Elite%20Drives%2C%20I%20would%20like%20to%20book%20a%20service%20appointment."
                target="_blank"
                rel="noreferrer"
                data-testid="nav-whatsapp-button"
                className="flex items-center gap-2 rounded-full border border-gold/30 px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] text-ivory"
              >
                <MessageCircle size={14} className="text-gold" /> WhatsApp
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
